/**
 * DangerZoneCard — destructive sample-data wipe form. Extracted from CMSEditor
 * so the container and SectionBody share it via the cms/ folder.
 */
import React from "react";
import { toast } from "sonner";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Code } from "./primitives";

export function DangerZoneCard() {
  const [confirmText, setConfirmText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const canReset = confirmText === "RESET" && !busy;

  const doReset = async () => {
    if (!canReset) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/system/wipe-sample-data", { confirm: "RESET" });
      setResult({ ok: true, report: data });
      setConfirmText("");
      toast.success("Sample data wiped successfully");
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || "Wipe failed" });
      toast.error(e.response?.data?.detail || "Wipe failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border-2 border-destructive/30 bg-destructive/5 p-4">
      <div className="text-sm font-black uppercase tracking-widest text-destructive">⚠ Danger zone — destructive</div>
      <div className="text-xs leading-relaxed text-muted-foreground">
        Removes <b>all</b> sample/demo data — orders, invoices, challans, ledger, leads, tickets, employees, tenders, bowsers, notifications, audit logs, etc. Keeps only the 5 essential accounts (<Code>admin</Code>, <Code>accounts</Code>, <Code>dispatch</Code>, <Code>driver</Code>, <Code>customer</Code>), their linked customer/driver records, and the CMS + integrations config. Counters reset (next order = ORD-000001). <b>Cannot be undone.</b>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="danger-confirm-input" className="text-xs font-bold uppercase tracking-widest">Type <Code>RESET</Code> to confirm</Label>
        <Input
          id="danger-confirm-input"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="RESET"
          className="font-mono"
          data-testid="danger-confirm-input"
        />
      </div>
      <Button
        variant="destructive"
        onClick={doReset}
        disabled={!canReset}
        data-testid="danger-reset-btn"
      >
        {busy ? "Wiping…" : "Wipe all sample data"}
      </Button>
      {result?.ok && (
        <details className="text-xs" open>
          <summary className="cursor-pointer font-bold text-success">✓ Wipe complete — click for report</summary>
          <pre className="mt-2 overflow-x-auto rounded bg-black/60 p-3 font-mono text-[10px] text-emerald-200">{JSON.stringify(result.report, null, 2)}</pre>
        </details>
      )}
      {result && !result.ok && <div className="text-xs font-semibold text-destructive">{result.error}</div>}
    </div>
  );
}
